/** Тема оформления — задаёт цветовую схему всего приложения */
export enum AppTheme {
  /** Тёмная цветовая схема */
  Dark = 'dark',
  /** Светлая цветовая схема */
  Light = 'light',
}

/**
 * Категория каталога — дропдаун хедера и страница `/catalog`
 * @prop id - Уникальный идентификатор категории
 * @prop title - Название категории (например, «Турники»)
 * @prop sub - Подзаголовок в дропдауне (например, «3 модели»). Необязателен
 * @prop slug - URL-slug для маршрута `/catalog/:slug`
 * @prop coverImage - Путь к обложке категории в `/public`
 */
export interface ICatalogCategory {
  id: string;
  title: string;
  sub?: string;
  slug: string;
  coverImage?: string;
  /** Раздел объявлен, но страницы ещё нет — показываем «скоро» вместо ссылки */
  isComingSoon?: boolean;
}

/**
 * Единица оборудования внутри категории каталога
 * @prop id - Уникальный идентификатор позиции
 * @prop categorySlug - Slug категории, к которой относится товар
 * @prop number - Номер позиции в списке раздела, не артикул производителя
 * @prop title - Название модели
 * @prop description - Краткое описание
 * @prop photos - Пути к фото — `/public` или data-URI
 * @prop videoUrl - Embed-ссылка Rutube для видеообзора
 * @prop videoTitle - Подпись о содержании ролика, проверенная по самому видео
 * @prop specs - Таблица технических характеристик
 * @prop exercises - Упражнения на снаряде: первое — основное, остальные — варианты
 * @prop safetyNote - Общая оговорка по безопасности под упражнениями
 * @prop setup - Настройка тренажёра перед занятием: нагрузка, сиденье
 * @prop composition - Состав обзорной зоны вместо инструкции к отдельному снаряду
 */
export interface IEquipmentItem {
  id: string;
  categorySlug: string;
  number: string;
  title: string;
  description: string;
  photos: string[];
  videoUrl?: string;
  videoTitle?: string;
  specs?: ISpec[];
  exercises?: IExercise[];
  safetyNote?: string;
  setup?: string[];
  composition?: IEquipmentComponent[];
}

/**
 * Объект на обзорном изображении спортивной зоны
 * @prop title - Название объекта или группы оборудования
 * @prop description - Назначение и особенности объекта на изображении
 * @prop itemId - Карточка оборудования в этом же разделе, если объект представлен отдельно
 */
export interface IEquipmentComponent {
  title: string;
  description: string;
  itemId?: string;
}

/**
 * Упражнение на снаряде — блок «Инструкция по выполнению упражнений»
 * @prop title - Название упражнения
 * @prop start - Исходное положение
 * @prop steps - Выполнение по шагам
 * @prop tips - Рекомендации: облегчение, ошибки, безопасность
 * @prop inVideo - Упражнение показано на видео позиции
 */
export interface IExercise {
  title: string;
  start: string;
  steps: string[];
  tips?: string[];
  inVideo?: boolean;
}

/**
 * Строка таблицы характеристик оборудования
 * @prop label - Название параметра (например, «Высота стойки»)
 * @prop value - Значение параметра (например, «2 400 мм»)
 */
export interface ISpec {
  label: string;
  value: string;
}

/**
 * Поставка по госконтракту — точка на карте поставок. Только факты из карточки контракта в ЕИС
 * @prop id - Реестровый номер контракта в ЕИС
 * @prop year - Год заключения контракта
 * @prop customer - Сокращённое наименование заказчика
 * @prop customerInn - ИНН заказчика: по нему считаются уникальные заказчики, названия в ЕИС пишут по-разному
 * @prop region - Субъект РФ так, как он назван в границах карты
 * @prop place - Населённый пункт из места поставки
 * @prop lat - Широта населённого пункта
 * @prop lon - Долгота населённого пункта
 * @prop inProgress - Контракт ещё исполняется
 */
export interface IDelivery {
  id: string;
  year: number;
  customer: string;
  customerInn: string;
  region: string;
  place: string;
  lat: number;
  lon: number;
  inProgress?: boolean;
}

/**
 * Знак заказчика в ленте «Нам доверяют»: собственный логотип организации или герб территории
 * @prop id - Слаг, совпадает с именем файла
 * @prop kind - logo — логотип организации-заказчика, coat — герб территории, где поставляли
 * @prop name - Название организации или территории, по нему строится alt
 * @prop logo - Путь к файлу в `/public/customers/`
 * @prop width - Ширина файла в пикселях (для SVG — из viewBox)
 * @prop height - Высота файла в пикселях (для SVG — из viewBox)
 * @prop source - Где взят оригинал: официальный сайт или страница организации
 */
export interface ICustomerLogo {
  id: string;
  kind: 'logo' | 'coat';
  name: string;
  logo: string;
  width: number;
  height: number;
  source: string;
}

/**
 * Реализованный объект — раздел «Проекты»
 * @prop id - Уникальный идентификатор проекта
 * @prop title - Название подтверждённого объекта
 * @prop location - Населённый пункт
 * @prop region - Регион размещения
 * @prop year - Год заключения контракта; дата открытия может отличаться
 * @prop contractId - Номер контракта в реестре ЕИС, связан с entities/delivery
 * @prop role - Только подтверждённый объём работ поставщика
 * @prop description - Собственный текст по публикациям об объекте
 * @prop equipment - Подтверждённые группы оборудования без предположений о моделях
 * @prop photos - Фото именно этого объекта; первое — обложка
 * @prop photoCredit - Автор или издание, указанные источником
 * @prop photoSourceUrl - Страница публикации фотографий
 * @prop sources - Публичные подтверждения поставки и открытия объекта
 */
export interface IProject {
  id: string;
  title: string;
  location: string;
  region: string;
  year: number;
  contractId: string;
  role: string;
  description: string;
  equipment: readonly string[];
  photos: readonly [IProjectPhoto, ...IProjectPhoto[]];
  photoCredit: string;
  photoSourceUrl: string;
  sources: readonly { label: string; url: string }[];
}

/**
 * Фото объекта, сохраняющее исходные пропорции
 * @prop src - Полноразмерный локальный WebP
 * @prop thumbnail - Превью шириной 640px
 * @prop width - Ширина полноразмерного файла в пикселях
 * @prop height - Высота полноразмерного файла в пикселях
 * @prop alt - Описание конкретного кадра
 */
export interface IProjectPhoto {
  src: string;
  thumbnail: string;
  width: number;
  height: number;
  alt: string;
}

/** `T | null` — неинициализированное или сброшенное значение */
export type Nullable<T> = T | null;

/** `T | undefined` — опциональное значение */
export type Optional<T> = T | undefined;
